/**
 * @fileoverview Filtre İşleme Yardımcı Fonksiyonları
 *
 * Bu modül, Emlakjet URL filtre string'lerini parse etme,
 * düzenleme ve label çevirme işlevlerini sağlar.
 * filter-labels.json dosyasından filtre kataloğunu okur.
 *
 * @module utils/filters
 */

import filterLabelsData from "./filter-labels.json";
import { parseFiltersToObject } from "./parsing";

/**
 * Filtre anahtar etiketleri sözlüğü.
 * Türkçe kullanıcı dostu etiketler için fallback.
 *
 * @constant {Object.<string, string>}
 */
export const FILTER_LABELS = {
  "oda-sayisi": "Oda Sayısı",
  "max-fiyat": "Max Fiyat",
  "min-fiyat": "Min Fiyat",
  "max-m2": "Max m²",
  "min-m2": "Min m²",
  "binanin-yasi": "Binanın Yaşı",
  "kat-sayisi": "Kat Sayısı",
  "bulundugu-kat": "Bulunduğu Kat",
  "isitma-tipi": "Isıtma Tipi",
  "esya-durumu": "Eşya Durumu",
  "kullanim-durumu": "Kullanım Durumu",
  "krediye-uygunluk": "Krediye Uygunluk",
  "tapu-durumu": "Tapu Durumu",
  "takas": "Takas",
  "semt": "Semt",
  "mahalle": "Mahalle",
  "ilce": "İlçe",
  "q": "Arama",
};

/**
 * Filtre anahtarı için okunabilir etiket döndürür.
 *
 * Önce filter-labels.json'dan, bulunamazsa FILTER_LABELS'dan,
 * en son fallback olarak slug'ı title case'e çevirir.
 *
 * @param {string} key - Filtre anahtarı (örn: "oda-sayisi")
 * @returns {string} Okunabilir etiket (örn: "Oda Sayısı")
 *
 * @example
 * getFilterLabel("oda-sayisi")  // "Oda Sayısı"
 * getFilterLabel("min-fiyat")   // "Min Fiyat"
 */
export function getFilterLabel(key) {
  // First check our JSON data
  if (filterLabelsData[key]?.key_label) {
    return filterLabelsData[key].key_label;
  }
  // Fallback to hardcoded labels
  return FILTER_LABELS[key] || key.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Get human-readable label for a filter value
 * @param {string} filterKey - The filter key (e.g., "oda-sayisi")
 * @param {string} valueSlug - The value slug (e.g., "3-1")
 * @returns {string} Human-readable label (e.g., "3+1")
 */
export function getFilterValueLabel(filterKey, valueSlug) {
  const filterData = filterLabelsData[filterKey];
  if (filterData?.values?.[valueSlug]) {
    return filterData.values[valueSlug];
  }
  // Fallback: try to humanize the slug
  return valueSlug
    .split("-")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Get all available values for a filter key
 * @param {string} filterKey - The filter key
 * @returns {Array<{slug: string, label: string}>} Array of value options
 */
export function getFilterOptions(filterKey) {
  const filterData = filterLabelsData[filterKey];
  if (!filterData?.values) return [];

  return Object.entries(filterData.values).map(([slug, label]) => ({
    slug,
    label
  }));
}

/**
 * Get filter catalog data
 * @returns {Object} The full filter labels data
 */
export function getFilterCatalog() {
  return filterLabelsData;
}

/**
 * Filtre string'ini parse ederek objeye çevirir.
 *
 * "key1=val1,val2&key2=val3" formatındaki string'i
 * { key1: ["val1", "val2"], key2: ["val3"] } şeklinde parse eder.
 *
 * @function parseFilterString
 * @param {string} raw - Ham filtre string'i
 * @returns {Object.<string, string[]>} Parse edilmiş filtre objesi
 *
 * @example
 * parseFilterString("oda-sayisi=3-1,4-1&min-fiyat=1000000")
 * // { "oda-sayisi": ["3-1", "4-1"], "min-fiyat": ["1000000"] }
 *
 * @see {@link module:utils/parsing.parseFiltersToObject}
 */
export const parseFilterString = parseFiltersToObject;

/**
 * Filtre string'inden belirli bir anahtarı kaldırır.
 *
 * @param {string} raw - Mevcut filtre string'i
 * @param {string} keyToRemove - Kaldırılacak anahtar
 * @returns {string} Güncellenmiş filtre string'i
 *
 * @example
 * removeFilterKey("oda-sayisi=3-1&min-fiyat=1000000", "oda-sayisi")
 * // "min-fiyat=1000000"
 */
export function removeFilterKey(raw, keyToRemove) {
  if (typeof raw !== "string" || raw.trim().length === 0) return "";
  if (typeof keyToRemove !== "string" || keyToRemove.trim().length === 0) return raw.trim();

  const pairs = raw
    .split("&")
    .map((p) => p.trim())
    .filter(Boolean);

  const kept = pairs.filter((pair) => {
    const [k] = pair.split("=");
    return k !== keyToRemove;
  });

  return kept.join("&");
}

/**
 * Display format → URL format mapping.
 * Özel durumlar için explicit mapping (decimal oda sayıları).
 *
 * @constant {Object.<string, string>}
 */
const DISPLAY_TO_URL_ROOM = {
  "1+0": "1-0",
  "1+1": "1-1",
  "1.5+1": "15-1",
  "2+0": "2-0",
  "2+1": "2-1",
  "2.5+1": "25-1",
  "3+0": "3-0",
  "3+1": "3-1",
  "3.5+1": "35-1",
  "4+0": "4-0",
  "4+1": "4-1",
  "4.5+1": "45-1",
  "5+1": "5-1",
  "5+2": "5-2",
  "6+1": "6-1",
  "6+2": "6-2",
  "6+3": "6-3",
  "7+": "7-",
};

/**
 * URL format → Display format mapping.
 * Özel durumlar için explicit mapping (decimal oda sayıları).
 *
 * @constant {Object.<string, string>}
 */
const URL_TO_DISPLAY_ROOM = {
  "1-0": "1+0",
  "1-1": "1+1",
  "15-1": "1.5+1",
  "2-0": "2+0",
  "2-1": "2+1",
  "25-1": "2.5+1",
  "3-0": "3+0",
  "3-1": "3+1",
  "35-1": "3.5+1",
  "4-0": "4+0",
  "4-1": "4+1",
  "45-1": "4.5+1",
  "5-1": "5+1",
  "5-2": "5+2",
  "6-1": "6+1",
  "6-2": "6+2",
  "6-3": "6+3",
  "7-": "7+",
};

/**
 * Oda sayısını URL formatına dönüştürür.
 *
 * Display format (3+1, 1.5+1) → URL format (3-1, 15-1)
 *
 * @param {string} roomLabel - Oda sayısı etiketi (örn: "3+1", "1.5+1")
 * @returns {string|null} URL slug'ı (örn: "3-1", "15-1") veya null
 *
 * @example
 * listingRoomToSlug("3+1")    // "3-1"
 * listingRoomToSlug("1.5+1")  // "15-1"
 * listingRoomToSlug("7+")     // "7-"
 */
export function listingRoomToSlug(roomLabel) {
  if (typeof roomLabel !== "string") return null;
  const trimmed = roomLabel.trim();
  if (!trimmed) return null;

  // Check explicit mapping first
  if (DISPLAY_TO_URL_ROOM[trimmed]) {
    return DISPLAY_TO_URL_ROOM[trimmed];
  }

  // Fallback: remove dots, replace + with -
  return trimmed.replace(/\./g, "").replace(/\+/g, "-");
}

/**
 * URL formatındaki oda sayısını display formatına dönüştürür.
 *
 * URL format (3-1, 15-1) → Display format (3+1, 1.5+1)
 *
 * @param {string} roomSlug - URL formatında oda sayısı (örn: "3-1", "15-1")
 * @returns {string|null} Display formatı (örn: "3+1", "1.5+1") veya null
 *
 * @example
 * slugToRoomDisplay("3-1")   // "3+1"
 * slugToRoomDisplay("15-1")  // "1.5+1"
 */
export function slugToRoomDisplay(roomSlug) {
  if (typeof roomSlug !== "string") return null;
  const trimmed = roomSlug.trim();
  if (!trimmed) return null;

  // Check explicit mapping first
  if (URL_TO_DISPLAY_ROOM[trimmed]) {
    return URL_TO_DISPLAY_ROOM[trimmed];
  }

  // Fallback: replace - with +
  return trimmed.replace(/-/g, "+");
}

/**
 * İlanın filtrelere neden uyduğunu açıklayan sebepler oluşturur.
 *
 * "Neden bu ilan?" özelliği için kullanılır. Fiyat, alan ve
 * oda sayısı filtrelerini kontrol ederek eşleşme sebeplerini listeler.
 *
 * @param {Object} listing - İlan verisi
 * @param {number} [listing.price] - İlan fiyatı
 * @param {number} [listing.m2] - İlan alanı
 * @param {string} [listing.room] - Oda sayısı
 * @param {Object.<string, string[]>} queryFilters - Aktif filtreler
 * @returns {string[]} Eşleşme sebepleri (max 2)
 *
 * @example
 * buildWhyThis(
 *   { price: 4000000, room: "3+1" },
 *   { "max-fiyat": ["5000000"], "oda-sayisi": ["3-1"] }
 * )
 * // ["Max bütçe içinde", "Oda sayısı eşleşiyor"]
 */
export function buildWhyThis(listing, queryFilters) {
  const reasons = [];
  if (!listing || !queryFilters) return reasons;

  const minPrice = Number(queryFilters["min-fiyat"]?.[0]);
  const maxPrice = Number(queryFilters["max-fiyat"]?.[0]);
  const price = typeof listing.price === "number" ? listing.price : null;
  if (price != null) {
    if (Number.isFinite(minPrice) && price >= minPrice) reasons.push("Min bütçe içinde");
    if (Number.isFinite(maxPrice) && price <= maxPrice) reasons.push("Max bütçe içinde");
  }

  const minM2 = Number(queryFilters["min-m2"]?.[0]);
  const maxM2 = Number(queryFilters["max-m2"]?.[0]);
  const m2 = typeof listing.m2 === "number" ? listing.m2 : null;
  if (m2 != null) {
    if (Number.isFinite(minM2) && m2 >= minM2) reasons.push("Min m² içinde");
    if (Number.isFinite(maxM2) && m2 <= maxM2) reasons.push("Max m² içinde");
  }

  const roomFilter = queryFilters["oda-sayisi"] ?? [];
  const roomSlug = listingRoomToSlug(listing.room);
  if (roomSlug && roomFilter.includes(roomSlug)) reasons.push("Oda sayısı eşleşiyor");

  return reasons.slice(0, 2);
}
